import { useMutation } from '@apollo/client';
import type {
  DataExportCreateScheduleInput,
  DataExportCreateScheduleMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { DataExportCreateScheduleDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useCreateDataExportScheduleTRPC } from './useCreateDataExportScheduleTRPC';

/**
 * Flag-gated wrapper for the `dataExportCreateSchedule` mutation. Defaults to
 * the legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. Both paths return the same
 * `DataExportCreateScheduleMutation` shape.
 *
 * The input is the generated GraphQL `DataExportCreateScheduleInput` — the shape
 * the settings tab already builds from the form — so the Apollo branch passes it
 * straight through and the tRPC twin narrows its nullable fields.
 *
 * NOTE: the schedule's `Status` / `CronArn` flip ASYNCHRONOUSLY on BOTH paths
 * (V2 via the Hasura event trigger, V3 via the `DATA_EXPORT_SCHEDULE_CREATED`
 * consumer), so the schedule is not yet active when this resolves. The tab
 * refetches the active schedule immediately and the read hooks keep it live from
 * there.
 *
 * Neither path evicted/invalidated caches historically — the tab drives its own
 * refetches. The tRPC twin additionally invalidates the two dataExport query
 * families so the flag-on path does not depend solely on those manual refetches.
 */
export const useCreateDataExportSchedule = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [createScheduleGraphQL, graphqlState] = useMutation(
    DataExportCreateScheduleDocument
  );

  const trpcMutation = useCreateDataExportScheduleTRPC();

  const createDataExportSchedule = async (
    input: DataExportCreateScheduleInput
  ): Promise<DataExportCreateScheduleMutation> => {
    if (trpcEnabled) {
      return trpcMutation.createDataExportSchedule(input);
    }

    const result = await createScheduleGraphQL({
      variables: { object: input },
    });
    if (!result.data) {
      throw new Error('Failed to create data export schedule');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      createDataExportSchedule,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    createDataExportSchedule,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
